import { useCallback, useEffect, useState } from 'react';
import { ASC, DESC } from 'common/constants/analyst-dashboards';
import { SortCriteria } from 'dashboard409a/common/constants/analyst-dashboard';
import { getInitialSortCriteriaFromStorage, saveSortCriteriaToStorage } from 'dashboard409a/utilities';

export interface UseMultiColumnSortProps {
  storageKey: string;
  defaultOrderBy: string;
  defaultOrder: 'asc' | 'desc';
}

export interface UseMultiColumnSortReturn {
  sortCriteria: SortCriteria[];
  orderBy: string;
  order: 'asc' | 'desc';
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string, addToSort?: boolean) => void;
}

export const useMultiColumnSort = ({
  storageKey,
  defaultOrderBy,
  defaultOrder,
}: UseMultiColumnSortProps): UseMultiColumnSortReturn => {
  // Initialize state from localStorage if available
  const initialSortCriteria = getInitialSortCriteriaFromStorage(storageKey, defaultOrderBy, defaultOrder);
  const [sortCriteria, setSortCriteria] = useState<SortCriteria[]>(initialSortCriteria);

  // Derive orderBy and order from first sort criteria for backward compatibility
  const orderBy = sortCriteria.length > 0 ? sortCriteria[0].property : defaultOrderBy;
  const order = sortCriteria.length > 0 ? sortCriteria[0].direction : defaultOrder;

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, property: string, addToSort?: boolean) => {
      if (addToSort) {
        // Add to existing sorts (Shift+Click)
        setSortCriteria(prev => {
          const existingIndex = prev.findIndex(criteria => criteria.property === property);

          if (existingIndex >= 0) {
            const newCriteria = [...prev];
            const existing = newCriteria[existingIndex];

            if (existing.direction === 'asc') {
              // Change to desc
              newCriteria[existingIndex] = { ...existing, direction: 'desc' };
              return newCriteria;
            }
            // Remove from sort (was desc, now remove)
            newCriteria.splice(existingIndex, 1);
            return newCriteria.length > 0 ? newCriteria : [{ property, direction: 'asc' }];
          }
          // Property doesn't exist - add it with asc direction
          return [...prev, { property, direction: 'asc' }];
        });
      } else {
        // Replace all sorts (regular click)
        const existingIndex = sortCriteria.findIndex(criteria => criteria.property === property);
        const isAsc = existingIndex >= 0 && sortCriteria[existingIndex].direction === 'asc';

        setSortCriteria([{ property, direction: isAsc ? DESC : ASC }]);
      }
    },
    [sortCriteria]
  );

  // Save sort criteria to localStorage whenever it changes
  useEffect(() => {
    saveSortCriteriaToStorage(storageKey, sortCriteria);
  }, [sortCriteria, storageKey]);

  return {
    sortCriteria,
    orderBy,
    order,
    handleRequestSort,
  };
};
